import { useCallback, useEffect, useMemo, useState } from 'react';

interface ItemWithTopic {
  topic?: string | null;
}

interface UseTopicsOptions {
  /** Items that may contain topics (action items) */
  items?: ItemWithTopic[];
  /** Pre-extracted topics (e.g., from filter options) */
  initialTopics?: string[];
}

interface UseTopicsReturn {
  /** All available topics (from items + custom added) */
  allTopics: string[];
  /** Handler to add a custom topic */
  handleAddCustomTopic: (topic: string) => void;
}

/**
 * Hook to manage topic state for action items.
 * Extracts topics from items and combines with custom user-added topics.
 *
 * Can accept either:
 * - `items`: Array of objects with optional `topic` field (will extract topics)
 * - `initialTopics`: Pre-extracted array of topic strings
 *
 * Custom topics are automatically cleaned up when they're no longer used by any item.
 */
export const useTopics = ({ items, initialTopics }: UseTopicsOptions): UseTopicsReturn => {
  const [customTopics, setCustomTopics] = useState<string[]>([]);

  // Extract topics from items if provided, otherwise use initialTopics
  const topicsFromSource = useMemo(() => {
    if (items) {
      return items.map((item) => item.topic).filter((topic): topic is string => Boolean(topic));
    }
    return initialTopics ?? [];
  }, [items, initialTopics]);

  // Clean up custom topics that are now in source data (they've been "promoted")
  // or that are no longer used anywhere
  useEffect(() => {
    if (customTopics.length === 0) return;

    const sourceSet = new Set(topicsFromSource);
    // Remove custom topics that have been added to actual items (now in source)
    const stillNeeded = customTopics.filter((topic) => !sourceSet.has(topic));

    if (stillNeeded.length !== customTopics.length) {
      setCustomTopics(stillNeeded);
    }
  }, [topicsFromSource, customTopics]);

  const allTopics = useMemo(() => {
    const combined = new Set([...topicsFromSource, ...customTopics]);
    return Array.from(combined).sort();
  }, [topicsFromSource, customTopics]);

  const handleAddCustomTopic = useCallback(
    (topic: string) => {
      if (!allTopics.includes(topic)) {
        setCustomTopics((prev) => [...prev, topic]);
      }
    },
    [allTopics],
  );

  return {
    allTopics,
    handleAddCustomTopic,
  };
};
